import { Account } from '@radixdlt/radix-dapp-toolkit';
import { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Form, Link, ShouldRevalidateFunctionArgs, useActionData, useFetcher, useLoaderData, useNavigation, useSubmit } from 'react-router';
import { useEffect, useRef, useState } from 'react';
import AccountSelector from '~/components/account-selector';
import TokenPrice from '~/components/token-price';
import TokenSelector from '~/components/token-selector';
import TransactionButton from '~/components/transaction-button';
import { usePrevious } from '~/hooks/usePrevious';
import { quote } from '~/lib/algorithm/defiplaza.server';
import { getPairByBaseToken, getPairs } from '~/lib/models/pair';
import { getToken, getTokens, SelectToken } from '~/lib/models/tokens';
import { generateSwapManifest } from '~/lib/manifests';
import { classNames, DEX_COMPONENT_ADDRESS, getIconUrl, QUOTE_TOKEN_RESOURCE_ADDRESS, ROYALTIES, XRD_RESOURCE_ADDRESS } from '~/lib/util';
import { useRadix } from '~/rdt/radixProvider';
import { loader as walletLoader } from '~/routes/wallet.$account';
import { fetchPairState } from '~/rdt/gateway';
import TokenRow from '~/components/token-row';
import PopoverHover from '~/components/popover';
import { fetchPrices } from '~/lib/astrolescent';
import { migrateComponents } from '~/lib/migrate';
import { setDbFromContext } from '~/lib/db';
import { trackEvent } from '~/lib/analytics';

const doneTypingInterval = 500;
const updateDataInterval = 10000;

let timeOutId: any = undefined;

export const meta: MetaFunction = () => {
   return [{ title: 'Swap - DefiPlaza Radix' }, { name: 'description', content: 'Swap the most popular Radix tokens on DefiPlaza' }];
};

export function shouldRevalidate({ defaultShouldRevalidate, formMethod }: ShouldRevalidateFunctionArgs) {
   if (formMethod == 'POST') {
      return false;
   }

   return defaultShouldRevalidate;
}

export async function loader({ context }: LoaderFunctionArgs) {   
   setDbFromContext(context);

   const allPairs = await getPairs();
   const pairs = allPairs.filter(p => p.Pair.dexAddress == DEX_COMPONENT_ADDRESS);
   const allTokens = pairs.map(p => p.Token);


   let xrd: SelectToken;
   let [dfp2]: SelectToken = await getToken(QUOTE_TOKEN_RESOURCE_ADDRESS);
   let tokens: SelectToken[] = [];

   for (let token of allTokens) {
      // if (token.lpToken) {
      //    continue;
      // }

      if (token.address === QUOTE_TOKEN_RESOURCE_ADDRESS) {
         dfp2 = token;
         continue;
      }
      if (token.address === XRD_RESOURCE_ADDRESS) {
         xrd = token;
         continue;
      }

      tokens.push(token);
   }

   if (dfp2 != undefined) {
      tokens.unshift(dfp2);
   }
   if (xrd != undefined) {
      tokens.unshift(xrd);
   }

   const inputAmount = 100;

   try {
      const { pair, pairState, basePoolState, quotePoolState } = await getStates(xrd.address);
      const [outputAmount, remainder, fee] = quote(pair, pairState, basePoolState, quotePoolState, inputAmount, false);

      // Calculate price impact based on Astrolescent data
      const priceBefore = await getPriceBefore(XRD_RESOURCE_ADDRESS, QUOTE_TOKEN_RESOURCE_ADDRESS);
      const priceAfter = outputAmount / inputAmount;
      const impact = (priceAfter - priceBefore) / priceBefore;

      // fetch Astrolescent
      const astroAmount = await fetchAstrolescent(tokens[0].address, tokens[1].address, inputAmount);

      return ({
         error: null,
         tokens,
         baseToken: {
            index: 0,
            amount: inputAmount,
         },
         quoteToken: {
            index: 1,
            amount: outputAmount,
         },
         priceImpact: impact,
         fee: [fee],
         royalties: ROYALTIES,
         astrolescent: astroAmount,
      });
   } catch (error) {
      console.log(error);
      
      return ({
         error: 'An unknown error occured, please try again.',
         fullError: error,
         tokens: tokens,
         baseToken: {
            index: 0,
            amount: 100,
         },
         quoteToken: {
            index: 0,
            amount: 0,
         },
         priceImpact: 0,
         fee: [0],
         royalties: 0,
         astrolescent: '0'
      });
   }
}

export default function SwapPage() {
   const { tokens, baseToken, quoteToken, priceImpact, fee, royalties, error, astrolescent } = useLoaderData<typeof loader>();
   const submit = useSubmit();
   const navigation = useNavigation();
   const formRef = useRef<HTMLFormElement>(null);
   const inputRef = useRef<HTMLInputElement>(null);
   const walletFetcher = useFetcher<typeof walletLoader>();

   const { sendTransaction } = useRadix();
   const actionData = useActionData<typeof action>();

   const [inputToken, setInputToken] = useState(tokens[baseToken.index]);
   const [outputToken, setOutputToken] = useState(tokens[quoteToken.index]);
   const [account, setAccount] = useState<string | null>(null);

   const previousBaseToken = usePrevious(tokens[baseToken.index]);

   function onAccount(account: Account) {
      setAccount(account.address);

      walletFetcher.load(`/wallet/${account.address}`);
   }

   useEffect(() => {
      // Only run on change not on set
      if (!previousBaseToken) {
         return;
      }
      
      fetchQuote(formRef.current);
   }, [inputToken, outputToken]);

   function debounceSubmit(event) {
      clearTimeout(timeOutId);

      const $form = event.currentTarget;

      timeOutId = setTimeout(() => {
         fetchQuote($form);
      }, doneTypingInterval);
   }

   function fetchQuote($form) {
      const formData = new FormData($form);

      submit(formData, {
         method: $form.getAttribute('method') ?? $form.method,
         action: $form.getAttribute('action') ?? $form.action,
      });
   }

   async function submitTransaction(event) {
      if (!account) {
         return;
      }

      event.preventDefault();

      const $form = event.currentTarget;
      const formData = new FormData($form);

      const transactionManifest = generateSwapManifest(account, formData.get('inputToken[address]'), +formData.get('inputAmount'), formData.get('outputToken[address]'));

      const result = await sendTransaction(transactionManifest, `Swap ${formData.get('inputToken[symbol]')} for ${formData.get('outputToken[symbol]')} on DefiPlaza`);

     trackEvent("send_tx", {
       type: "swap",
     });

      walletFetcher.load(`/wallet/${account}`);
      fetchQuote(formRef.current);
   }

   function setMax() {
      inputRef.current.value = walletFetcher.data?.filter((b) => b.address == inputToken.address)[0]?.amount;

      fetchQuote(formRef.current);
   }

   function switchTokens(event) {
      event.preventDefault();

      setInputToken(outputToken);
      setOutputToken(inputToken);
   }

   return (
     <>
       <Form method="post" ref={formRef} onSubmit={submitTransaction} onChange={debounceSubmit} className="mx-4 mb-8 lg:mx-auto lg:w-3/4 lg:w-4/5 xl:w-1/2 2xl:w-2/5">
         {walletFetcher.data && walletFetcher.data?.filter((t) => t.migration).length > 0 ? (
           <section className="flex items-center justify-between rounded-xl bg-white p-4 text-black lg:rounded-2xl">
             <p className="flex flex-col">
               🆕 Some tokens in your wallet can be migrated to a new version:
               <span className="flex items-center space-x-2">
                 {walletFetcher.data
                   ?.filter((t) => t.migration)
                   .map((t) => (
                     <span className="flex items-center space-x-1">
                       <img src={getIconUrl(t.iconUrl, 16)} alt="" className="h-4 w-4 rounded-full" />
                       <strong>{t.name}</strong>
                     </span>
                   ))}
               </span>
             </p>
             <Link
               to={`/migrate`}
               className="text-md text-primary-500 border-primary-500 hover:border-primary-500 rounded-full border px-4 py-3 font-medium uppercase leading-none tracking-wider hover:bg-white"
             >
               Migrate
             </Link>
           </section>
         ) : null}
         <h1 className="py-4 text-left text-3xl font-bold lg:p-8 lg:text-4xl">Swap</h1>
         <div className="space-y-4">
           <AccountSelector mode="light" onAccount={onAccount}></AccountSelector>

           <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
             <div className="flex items-center justify-between">
               <label htmlFor="inputAmount" className="text-sm text-purple-800">
                 From
               </label>
               {walletFetcher.data ? (
                 <span className="w-60 truncate text-right text-sm font-normal text-black lg:text-left">
                   Balance: {(Math.round((walletFetcher.data.filter((b) => b.address == inputToken.address)[0]?.amount || 0) * 1000000) / 1000000).toLocaleString()}
                 </span>
               ) : null}
             </div>
             <div className="flex flex-wrap items-center justify-between">
               <input
                 ref={inputRef}
                 type="number"
                 inputMode="decimal"
                 step="any"
                 id="inputAmount"
                 name="inputAmount"
                 defaultValue={baseToken.amount}
                 placeholder="0.00"
                 autoComplete="off"
                 spellCheck="false"
                 className="mr-4 w-1/2 flex-auto border-none bg-transparent p-0 text-4xl text-black placeholder:text-purple-100 lg:w-60 dark:placeholder:text-purple-800/80"
               />

               {walletFetcher.data && walletFetcher.data.filter((b) => b.address == inputToken.address).length > 0 ? (
                 <button type="button" onClick={setMax} className="border-primary-500 text-primary-500 hover:bg-primary-500 rounded-full border px-3 py-1 text-xs hover:text-white sm:text-sm lg:mr-4">
                   MAX
                 </button>
               ) : null}

               <div className="w-full lg:w-40">
                 <TokenSelector name="inputToken" tokens={tokens} selected={inputToken} onSelected={setInputToken} />
               </div>
             </div>
           </section>
           {error || actionData?.error ? <p className="shadow-xs text-md mt-2 rounded-md bg-red-800 px-4 py-1 text-center text-base font-medium text-white">{error || actionData?.error}</p> : null}

           <p className="flex justify-between px-8">
             <svg className="h-6 w-6" viewBox="8 8 24 24" xmlns="http://www.w3.org/2000/svg">
               <path
                 d="M29.088 21.972c.432-.443.432-1.212 0-1.654a1.128 1.128 0 0 0-1.617 0l-6.322 6.466V10.58c0-.645-.513-1.17-1.144-1.17-.63 0-1.143.525-1.143 1.17v16.203l-6.321-6.466a1.128 1.128 0 0 0-1.618 0 1.178 1.178 0 0 0-.335.827c0 .312.12.606.336.827l7.964 8.143a1.556 1.556 0 0 0 2.237 0l7.963-8.143z"
                 fill="currentColor"
                 fillRule="nonzero"
               ></path>
             </svg>

             <button type="button" onClick={switchTokens} className="h-6 w-6 hover:scale-125">
               <svg viewBox="7 6 26 30" xmlns="http://www.w3.org/2000/svg">
                 <path
                   d="M32.361 29.38a.852.852 0 0 0 0-1.157.79.79 0 0 0-1.132 0l-4.425 4.526V21.407a.81.81 0 0 0-.8-.819.81.81 0 0 0-.8.819v11.342l-4.426-4.526a.79.79 0 0 0-1.132 0 .825.825 0 0 0-.234.579c0 .218.083.424.235.578l5.575 5.7a1.089 1.089 0 0 0 1.566 0l5.573-5.7zM7.639 12.62a.852.852 0 0 0 0 1.157.79.79 0 0 0 1.132 0l4.425-4.526v11.342c0 .452.36.819.8.819.441 0 .8-.367.8-.819V9.251l4.426 4.526a.79.79 0 0 0 1.132 0 .825.825 0 0 0 .234-.579.824.824 0 0 0-.235-.578l-5.575-5.7a1.089 1.089 0 0 0-1.566 0l-5.573 5.7z"
                   fill="currentColor"
                   fillRule="nonzero"
                 ></path>
               </svg>
             </button>
           </p>

           <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
             <label htmlFor="outputAmount" className="text-sm text-purple-800">
               To (estimated)
             </label>
             <div className="flex flex-wrap items-center justify-between">
               <input
                 type="number"
                 inputMode="decimal"
                 id="outputAmount"
                 name="outputAmount"
                 value={(actionData ? actionData.quoteToken.amount : quoteToken.amount)?.toFixed(4)}
                 placeholder="0.00"
                 autoComplete="off"
                 spellCheck="false"
                 disabled={true}
                 className={classNames(
                   navigation.state == "submitting" ? "animate-pulse rounded-sm bg-gray-200" : "bg-transparent",
                   "mr-4 w-1/2 flex-auto border-none p-0 text-4xl text-black placeholder:text-purple-100 lg:w-60 dark:placeholder:text-purple-800/80",
                 )}
               />

               <p
                 className={classNames(
                   (actionData ? actionData.priceImpact : priceImpact) < 0 ? "border-red-600 bg-red-100 text-red-600" : "border-green-600 bg-green-100 text-green-600",
                   "w-20 flex-none rounded-full px-3 py-1 text-center text-xs sm:border sm:text-sm lg:mr-4",
                 )}
               >
                 {((actionData ? actionData.priceImpact : priceImpact) * 100).toFixed(2) + "%"}
               </p>

               <div className="w-full lg:w-40">
                 <TokenSelector name="outputToken" tokens={tokens} selected={outputToken} onSelected={setOutputToken} />
               </div>
             </div>
           </section>

           <div className="flex items-center justify-between space-x-4 px-8 lg:justify-center">
             <p className="text-xs font-bold lg:text-base">
               Price:{" "}
               <TokenPrice
                 priceData={{
                   inputToken,
                   outputToken,
                   inputAmount: actionData ? actionData.baseToken.amount : baseToken.amount,
                   outputAmount: actionData ? actionData.quoteToken.amount : quoteToken.amount,
                 }}
                 onClick={() => {}}
               />
             </p>
             <p className="text-xs font-bold lg:text-base">
               Fee:{" "}
               <span className="font-normal">
                 {(actionData ? actionData.fee.length == 2 : fee.length == 2) ? <>{(actionData ? actionData.fee[0] : fee[0]).toFixed(2)} DFP2 + </> : null}
                 {(actionData ? actionData.fee[actionData.fee.length - 1] : fee[fee.length - 1]).toFixed(2)} {outputToken.symbol} + {(actionData ? actionData.royalties : royalties).toFixed(2)} XRD in
                 Royalties
               </span>
             </p>
           </div>

           <TransactionButton title="Swap" onClick={() => {}} />

           {/* <div className="flex justify-between lg:justify-center items-center px-8 space-x-4">
               <p className="text-xs lg:text-base font-bold">
                  Minimum received:{' '}
                  <span className="text-xs lg:text-base font-normal">
                     {((actionData ? actionData.quoteToken.amount : quoteToken.amount) * 0.99).toFixed(4)} {outputToken.symbol}
                  </span>
               </p>
               
            </div> */}
         </div>
         <section className="bg-primary-800 mt-16 flex flex-col rounded-xl p-4 lg:rounded-2xl lg:p-8">
           <label htmlFor="outputAmount" className="text-primary-100 text-sm">
             <PopoverHover title="Or buy on Astrolescent">
               <div className="w-40 space-y-2">
                 <p>
                   Using an aggregator like Astrolescent is in general a better idea for traders than using a single DEX. We believe in sustainable DeFi and part of that is being transparent to all
                   our users.
                 </p>
                 <p>
                   DefiPlaza is optimized to reduce the risk of Impermanent Loss and be sustainably profitable for LPs. This means swaps are not always the best deal for traders, when they are the
                   best deal for LPs.
                 </p>
                 <p>
                   <a href="https://docs.defiplaza.net/defiplaza/radix/faq" target="blank" className="underline">
                     Learn more
                   </a>
                 </p>
               </div>
             </PopoverHover>
           </label>
           <div className="flex flex-wrap items-center justify-between">
             <div className="flex items-center">
               <p
                 className={classNames(
                   navigation.state == "submitting" ? "animate-pulse rounded-sm bg-gray-200" : "bg-transparent",
                   "placeholder:text-primary-500/80 mr-4 w-1/2 border-none p-0 text-4xl text-gray-200 lg:w-40",
                 )}
               >
                 {(actionData ? actionData.astrolescent : astrolescent)?.toFixed(4)}
               </p>
               <div className="-mr-4 w-full lg:w-1/3">
                 <TokenRow token={outputToken} darkMode={true} />
               </div>
             </div>

             <a
               href={`https://astrolescent.com?from=${inputToken.address}&to=${outputToken.address}&amount=${actionData ? actionData.baseToken.amount : baseToken.amount}`}
               target="_blank"
               className="btn-white w-min px-4 py-3"
             >
               Buy
             </a>
           </div>
         </section>
       </Form>
     </>
   );
}

export async function action({ request, context }: ActionFunctionArgs) {
   setDbFromContext(context);

   const formData = await request.formData();
   const inputAmount = parseFloat(formData.get('inputAmount'));

   const inputAddress = formData.get('inputToken[address]') as string;
   const outputAddress = formData.get('outputToken[address]') as string;

   try {
      let outputAmount = 0;
      let priceImpact = 0;
      let feeAmount = [0];
      let royalties = ROYALTIES;

      const inputDFP2 = inputAddress === QUOTE_TOKEN_RESOURCE_ADDRESS;
      const outputDFP2 = outputAddress === QUOTE_TOKEN_RESOURCE_ADDRESS;

      // Calculate price impact based on Astrolescent data
      const priceBefore = await getPriceBefore(inputAddress, outputAddress);

      if (inputDFP2 && outputDFP2) {
         outputAmount = inputAmount;
      } else if (!inputDFP2 && outputDFP2) {
         const { pair, pairState, basePoolState, quotePoolState } = await getStates(inputAddress);
         const [output, remainder, fee] = quote(pair, pairState, basePoolState, quotePoolState, inputAmount, false);

         outputAmount = output;
         const priceAfter = outputAmount / inputAmount;
         priceImpact = (priceAfter - priceBefore) / priceBefore;
         feeAmount = [fee];
      } else if (inputDFP2 && !outputDFP2) {
         const { pair, pairState, basePoolState, quotePoolState } = await getStates(outputAddress);
         const [output, remainder, fee] = quote(pair, pairState, basePoolState, quotePoolState, inputAmount, true);

         outputAmount = output;
         const priceAfter = outputAmount / inputAmount;
         priceImpact = (priceAfter - priceBefore) / priceBefore;
         feeAmount = [fee];
      } else {
         // !inputDFP2 && !outputDFP2
         // Hop!
         const pair1 = await getStates(inputAddress);
         const pair2 = await getStates(outputAddress);

         const [amount1, remainder1, fee1] = quote(pair1.pair, pair1.pairState, pair1.basePoolState, pair1.quotePoolState, inputAmount, false);
         const [amount2, remainder2, fee2] = quote(pair2.pair, pair2.pairState, pair2.basePoolState, pair2.quotePoolState, amount1, true);

         outputAmount = amount2;
         const priceAfter = outputAmount / inputAmount;
         priceImpact = (priceAfter - priceBefore) / priceBefore;
         feeAmount = [fee1, fee2];
         royalties = 2 * ROYALTIES;
      }

      const astroAmount = await fetchAstrolescent(inputAddress, outputAddress, inputAmount);

      let error;

      if (migrateComponents[inputAddress] && migrateComponents[inputAddress]?.new == outputAddress) {
         error = 'Please migratie the token via the button on the top of this screen instead.';
      } 

      return ({
         error: error,
         baseToken: {
            amount: inputAmount,
         },
         quoteToken: {
            amount: outputAmount,
         },
         priceImpact,
         fee: feeAmount,
         royalties,
         astrolescent: astroAmount,
      });
   } catch (error) {
      console.log(error);

      return ({
         error: 'An unknown error occured, please try again.',
         baseToken: {
            amount: inputAmount,
         },
         quoteToken: {
            amount: 0,
         },
         priceImpact: 0,
         fee: [0],
         royalties: 0,
         astrolescent: ''
      });
   }
}

async function getStates(address: string) {
   const [row] = await getPairByBaseToken(address);
   const result = await fetchPairState(row.Pair);

   return {
      pair: row.Pair,
      ...result,
   };
}

async function getPriceBefore(inputToken, outputToken) {
   const prices = await fetchPrices();

   if (!prices[inputToken]?.tokenPriceXRD || !prices[outputToken]?.tokenPriceXRD) {
      return 0;
   }

   return prices[inputToken].tokenPriceXRD / prices[outputToken].tokenPriceXRD;
}

async function fetchAstrolescent(inputTokenAddress: string, outputTokenAddress: string, inputAmount: number) {
   try {
      const response = await fetch('https://api.astrolescent.com/partner/defiplaza/swap', {
         method: 'POST',
         body: JSON.stringify({
            inputToken: inputTokenAddress,
            outputToken: outputTokenAddress,
            inputAmount: inputAmount,
         }),
      });
      const result = await response.json();

      if (result.error) {
         return 0;
      }

      return result.outputTokens;
   }
   catch (err) {
      return 0;
   }
}
